import { useCallback, useMemo, useState } from "react";
import styles from "./PromotionBanner.module.scss";
import { PromotionBannerData } from "src/types/graphql.d";
import { PromotionBannerTypes } from "src/types/PromotionBannerTypes";
import PromotionBannerTypeOne from "src/molecules/PromotionBanner/PromotionBannerTypeOne";
import PromotionBannerTypeTwo from "src/molecules/PromotionBanner/PromotionBannerTypeTwo";
import PromotionBannerTypeThree from "src/molecules/PromotionBanner/PromotionBannerTypeThree";
import { FlashSaleEndDialog } from "src/molecules/FlashSaleEndDialog";
import { BannerTimer } from "./Variations/BannerTimer";
import { useRouter } from "next/router";
import { ConditionalLink } from "src/atoms/ConditionalLink";
import { trackClickedPromotionEvent } from "src/tracking/events/clicked-promotion";
import { PROMOTION_TYPE } from "src/utils/constants";
import { getFileName } from "src/utils/getFileName";
import { useInView } from "react-intersection-observer";
import { trackViewedPromotionEvent } from "src/tracking/events/viewed-promotion";
import type { BannerImageSet } from "./PromotionBannerTypes";

type PromotionBannerProps = {
  promotionBanner: PromotionBannerData;
  bannerPosition: number;
  priority?: boolean;
};

function PromotionBanner({
  promotionBanner,
  bannerPosition,
  priority = false,
}: PromotionBannerProps): JSX.Element {
  const [hasTimerEnded, setTimerEnded] = useState(false);
  const [showFlashSaleEndModal, setShowFlashSaleEndModal] = useState(false);
  const router = useRouter();

  const images: BannerImageSet = useMemo(
    () => ({
      mobile: promotionBanner.mobile_image,
      tablet: promotionBanner.tablet_image,
      desktop: promotionBanner.desktop_image,
      mobileWidth: promotionBanner.mobile_image_width,
      mobileHeight: promotionBanner.mobile_image_height,
      tabletWidth: promotionBanner.tablet_image_width,
      tabletHeight: promotionBanner.tablet_image_height,
    }),
    [
      promotionBanner.mobile_image,
      promotionBanner.tablet_image,
      promotionBanner.desktop_image,
      promotionBanner.mobile_image_width,
      promotionBanner.mobile_image_height,
      promotionBanner.tablet_image_width,
      promotionBanner.tablet_image_height,
    ]
  );

  const trackingData = useMemo(
    () => ({
      name: promotionBanner.title || promotionBanner.description || "",
      creative: getFileName(promotionBanner.desktop_image),
      position: `${PROMOTION_TYPE.PROMOTION_BANNER}-${bannerPosition}`,
      promotionType: PROMOTION_TYPE.PROMOTION_BANNER,
      promoDisplayType: promotionBanner.type,
    }),
    [
      promotionBanner.title,
      promotionBanner.description,
      promotionBanner.desktop_image,
      bannerPosition,
      promotionBanner.type,
    ]
  );

  const [ref] = useInView({
    threshold: 0,
    triggerOnce: true,
    onChange(inView) {
      if (!inView) {
        return;
      }

      void trackViewedPromotionEvent({ ...trackingData });
    },
  });

  const onTimerEnd = (hasTimerEnded: boolean) => setTimerEnded(hasTimerEnded);

  const showTimerEndModal = useCallback(
    () => setShowFlashSaleEndModal(true),
    []
  );

  const hideTimerEndModal = useCallback(() => {
    router.reload();
    setShowFlashSaleEndModal(false);
  }, [router]);

  const handleClick = useCallback(() => {
    if (!promotionBanner.hide_cta_button && hasTimerEnded) {
      showTimerEndModal();
    }

    void trackClickedPromotionEvent({
      ...trackingData,
    });
  }, [
    showTimerEndModal,
    promotionBanner.hide_cta_button,
    hasTimerEnded,
    trackingData,
  ]);

  return (
    <div
      key={`${promotionBanner.type}_${
        promotionBanner.title || promotionBanner.description || ""
      }`}
      className={styles.wrapper}
      onClick={handleClick}
      ref={ref}
    >
      <ConditionalLink
        condition={!hasTimerEnded || !promotionBanner.hide_cta_button}
        href={promotionBanner.banner_link as string}
      >
        {promotionBanner.type === PromotionBannerTypes.Type_One && (
          <PromotionBannerTypeOne
            priority={priority}
            images={images}
            title={promotionBanner.title}
            description={promotionBanner.description}
            buttonTitle={promotionBanner.button_title}
            buttonColor={promotionBanner.button_colour}
            buttonTextColor={promotionBanner.button_text_colour}
            conditionText={promotionBanner.condition_text}
            hideCTAButton={promotionBanner.hide_cta_button || false}
            disableBackgroundColor={
              promotionBanner.disable_bg_color_desktop || false
            }
            textAlignment={promotionBanner.text_alignment}
            textColor={promotionBanner.text_colour}
            titleColor={promotionBanner.title_color || "tone.black"}
          />
        )}
        {promotionBanner.type === PromotionBannerTypes.Type_Two && (
          <PromotionBannerTypeTwo
            priority={priority}
            images={images}
            title={promotionBanner.title}
            buttonTitle={promotionBanner.button_title}
            buttonColor={promotionBanner.button_colour}
            buttonTextColor={promotionBanner.button_text_colour}
            conditionText={promotionBanner.condition_text}
            description={promotionBanner.description}
            backgroundColor={promotionBanner.background_colour || "tone.black"}
            hideCTAButton={promotionBanner.hide_cta_button || false}
            textColor={promotionBanner.text_colour}
            titleColor={promotionBanner.title_color || "tone.black"}
          />
        )}

        {promotionBanner.type === PromotionBannerTypes.Type_Three && (
          <PromotionBannerTypeThree
            priority={priority}
            images={images}
            title={promotionBanner.title}
            buttonTitle={promotionBanner.button_title}
            buttonColor={promotionBanner.button_colour}
            buttonTextColor={promotionBanner.button_text_colour}
            conditionText={promotionBanner.condition_text}
            description={promotionBanner.description}
            backgroundColor={promotionBanner.background_colour || "tone.black"}
            textColor={promotionBanner.text_colour}
            promoCode={promotionBanner.promo_code}
            promoCodeColor={promotionBanner.promo_code_colour}
            hideCTAButton={promotionBanner.hide_cta_button || false}
            disableBackgroundColor={
              promotionBanner.disable_bg_color_desktop || false
            }
            textAlignment={promotionBanner.text_alignment}
            titleColor={promotionBanner.title_color || "tone.black"}
            disablePromoCodeComponent={
              !promotionBanner?.promo_code ||
              promotionBanner?.disable_promo_code_component ||
              false
            }
          />
        )}

        {promotionBanner.type === PromotionBannerTypes.Type_Timer ? (
          <>
            <BannerTimer
              priority={priority}
              images={images}
              title={promotionBanner.title}
              buttonTitle={promotionBanner.button_title}
              buttonColor={promotionBanner.button_colour}
              buttonTextColor={promotionBanner.button_text_colour}
              conditionText={promotionBanner.condition_text}
              description={promotionBanner.description}
              backgroundColor={
                promotionBanner.background_colour || "tone.black"
              }
              startDate={promotionBanner.start_date || ""}
              endDate={promotionBanner.end_date || ""}
              onTimerEnd={
                !promotionBanner.hide_cta_button ? onTimerEnd : undefined
              }
              hideCTAButton={promotionBanner.hide_cta_button || false}
              textColor={promotionBanner.text_colour}
              titleColor={promotionBanner.title_color || "tone.black"}
            />
            {hasTimerEnded ? (
              <FlashSaleEndDialog
                isOpen={showFlashSaleEndModal}
                onClose={hideTimerEndModal}
              />
            ) : null}
          </>
        ) : null}
      </ConditionalLink>
    </div>
  );
}

export default PromotionBanner;
